import { UserRole } from '@/contexts/AuthContext';

/**
 * Permission matrix for each module page.
 * - admin: full access to everything
 * - manager: access to all operational modules, no user management or audit logs
 * - viewer: read-only access to dashboard, sales, inventory, products, warehouses
 */
export type ModuleKey =
  | 'dashboard' |'sales-revenue' |'inventory-management' |'warehouses' |'products' |'profitability' |'advertising' |'returns' |'listings' |'unauthorized-sellers' |'settlements' |'reports' |'alerts' |'upload-centre' |'import-schedules' |'integrations' |'users' |'settings' |'audit-logs' |'workspace';

export interface ModulePermission {
  /** Roles allowed to VIEW this page at all */
  allowedRoles: UserRole[];
  /** Roles allowed to perform write/edit/delete actions */
  writeRoles: UserRole[];
  /** Human-readable label for the module */
  label: string;
}

export const MODULE_PERMISSIONS: Record<ModuleKey, ModulePermission> = {
  'dashboard':            { allowedRoles: ['admin', 'manager', 'viewer'], writeRoles: ['admin'], label: 'Dashboard' },
  'sales-revenue':        { allowedRoles: ['admin', 'manager', 'viewer'], writeRoles: ['admin', 'manager'], label: 'Sales & Revenue' },
  'inventory-management': { allowedRoles: ['admin', 'manager', 'viewer'], writeRoles: ['admin', 'manager'], label: 'Inventory' },
  'warehouses':           { allowedRoles: ['admin', 'manager', 'viewer'], writeRoles: ['admin', 'manager'], label: 'Warehouses' },
  'products':             { allowedRoles: ['admin', 'manager', 'viewer'], writeRoles: ['admin', 'manager'], label: 'Products' },
  'profitability':        { allowedRoles: ['admin', 'manager'], writeRoles: ['admin'], label: 'Profitability' },
  'advertising':          { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Advertising' },
  'returns':              { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Returns' },
  'listings':             { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Listings' },
  'unauthorized-sellers': { allowedRoles: ['admin', 'manager'], writeRoles: ['admin'], label: 'Unauthorized Sellers' },
  'settlements':          { allowedRoles: ['admin', 'manager'], writeRoles: ['admin'], label: 'Settlements' },
  'reports':              { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Reports' },
  'alerts':               { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Alerts' },
  'upload-centre':        { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Upload Centre' },
  'import-schedules':     { allowedRoles: ['admin', 'manager'], writeRoles: ['admin', 'manager'], label: 'Import Schedules' },
  'integrations':         { allowedRoles: ['admin'], writeRoles: ['admin'], label: 'Integrations' },
  'users':                { allowedRoles: ['admin'], writeRoles: ['admin'], label: 'Users' },
  'settings':             { allowedRoles: ['admin'], writeRoles: ['admin'], label: 'Settings' },
  'audit-logs':           { allowedRoles: ['admin'], writeRoles: ['admin'], label: 'Audit Logs' },
  'workspace':            { allowedRoles: ['admin'], writeRoles: ['admin'], label: 'Workspace' },
};

/** Check if a role can view a module */
export function canView(role: UserRole, module: ModuleKey): boolean {
  if (!role) return false;
  return MODULE_PERMISSIONS[module].allowedRoles.includes(role);
}

/** Check if a role can write/edit in a module */
export function canWrite(role: UserRole, module: ModuleKey): boolean {
  if (!role) return false;
  return MODULE_PERMISSIONS[module].writeRoles.includes(role);
}

/** Get all modules a role can access */
export function getAccessibleModules(role: UserRole): ModuleKey[] {
  if (!role) return [];
  return (Object.keys(MODULE_PERMISSIONS) as ModuleKey[]).filter(
    (mod) => MODULE_PERMISSIONS[mod].allowedRoles.includes(role)
  );
}

/** Map nav href to module key */
export function hrefToModuleKey(href: string): ModuleKey | null {
  const map: Record<string, ModuleKey> = {
    '/': 'dashboard',
    '/sales-revenue': 'sales-revenue',
    '/inventory-management': 'inventory-management',
    '/warehouses': 'warehouses',
    '/products': 'products',
    '/profitability': 'profitability',
    '/advertising': 'advertising',
    '/returns': 'returns',
    '/listings': 'listings',
    '/unauthorized-sellers': 'unauthorized-sellers',
    '/settlements': 'settlements',
    '/reports': 'reports',
    '/alerts': 'alerts',
    '/upload-centre': 'upload-centre',
    '/import-schedules': 'import-schedules',
    '/integrations': 'integrations',
    '/users': 'users',
    '/settings': 'settings',
    '/audit-logs': 'audit-logs',
    '/workspace': 'workspace',
  };
  return map[href] ?? null;
}
